import { Form, Head } from "@inertiajs/react"
import { useState } from "react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { onboardingTryPath } from "@/routes"

interface Props {
  products: string[]
  auto_select_products: boolean
}

const PRODUCT_LABELS: Record<string, string> = {
  rail_maintenance: "Rail Maintenance",
}

export default function TryDemo({ products, auto_select_products }: Props) {
  // On a single-line deployment there's no choice to make — the whole product
  // line is preselected and the picker is hidden.
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    auto_select_products ? products : [],
  )

  // Prefilled when arriving from the Google SSO "stranger" hand-off
  // (SessionsOauthController redirects unknown emails here with ?email=).
  const prefilledEmail =
    typeof window !== "undefined"
      ? (new URLSearchParams(window.location.search).get("email") ?? "")
      : ""

  const togglesInArray = (
    arr: string[],
    setter: (v: string[]) => void,
    value: string,
  ) => {
    setter(
      arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
    )
  }

  return (
    <AuthLayout
      title="Try Rebulk"
      description={
        auto_select_products
          ? "Drop your work email and we'll spin up a fully-seeded demo for you."
          : "Pick a product, drop your work email, get a fully-seeded demo tenant."
      }
    >
      <Head title="Try Rebulk" />
      <Form
        method="post"
        action={onboardingTryPath()}
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                placeholder="ops@yourcompany.com"
                defaultValue={prefilledEmail}
              />
              <InputError messages={errors.email} />
            </div>

            {auto_select_products ? (
              <div className="bg-muted/40 rounded-md border p-3 text-sm">
                <span className="text-muted-foreground">
                  Your demo includes
                </span>{" "}
                <span className="font-medium">
                  {products.map((p) => PRODUCT_LABELS[p] ?? p).join(" · ")}
                </span>
              </div>
            ) : (
              <fieldset className="grid gap-3">
                <legend className="text-sm font-semibold">
                  What do you want to track?
                </legend>
                {products.map((p) => (
                  <label
                    key={p}
                    className="hover:bg-muted flex cursor-pointer items-start gap-3 rounded-md border p-3"
                  >
                    <Checkbox
                      name="products[]"
                      value={p}
                      checked={selectedProducts.includes(p)}
                      onCheckedChange={() =>
                        togglesInArray(selectedProducts, setSelectedProducts, p)
                      }
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {PRODUCT_LABELS[p] ?? p}
                      </div>
                    </div>
                  </label>
                ))}
                <InputError messages={errors.products} />
              </fieldset>
            )}

            <Button
              type="submit"
              className="mt-2 w-full"
              disabled={processing || selectedProducts.length === 0}
            >
              {processing && <Spinner />}
              Send me the demo link
            </Button>
          </>
        )}
      </Form>
    </AuthLayout>
  )
}
